'use client'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { useRouter } from 'next/navigation'

import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6Props } from '@src/components/TopNavV6/TopNavV6Props'
import { useSession } from '@src/hooks/useSession'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

import { TopNavV6MobileFooter } from './TopNavV6MobileFooter'

export const TopNavV6MobileMenu = (props: TopNavV6Props) => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const { navItems, mobileSecondaryMenuItems } = props
  const { setIsMobileMenuOpen, setSelectedThirdLevelMenu } = useTopNavV6()
  const { user } = useSession()
  const isLoggedIn = Boolean(user?.email)
  const router = useRouter()

  // Get first 3 items and reorder so active item is first
  const firstThreeItems = navItems?.slice(0, 3) ?? []
  const activeItem = firstThreeItems.find((item) => item.isActive)
  const otherItems = firstThreeItems.filter((item) => !item.isActive)
  const mainMenuItems = activeItem ? [activeItem, ...otherItems] : firstThreeItems

  // Get child items of the active item
  const activeItemChildren = activeItem?.navItems ?? []

  return (
    <>
      <div className="flex flex-col gap-8 px-4 pb-8">
        {/* Main Menu Items */}
        <div className="flex flex-col">
          {mainMenuItems.map((item, index) => {
            const isActive = item.isActive
            return (
              <div
                key={`main-menu-${index}`}
                className={cn('flex flex-col', {
                  '-mx-4 gap-4 bg-bodi-black-text bg-gradient-to-r from-white/[0.08] to-white/[0.08] px-4 pb-8':
                    isActive,
                })}
              >
                <button
                  className={cn(
                    'flex w-full items-center justify-between border-b border-bodi-gray-dark px-0 py-3 text-left',
                    isActive && 'h-14'
                  )}
                  onClick={() => {
                    const url =
                      isLoggedIn && item.authenticatedHref ? item.authenticatedHref : item.href
                    if (url) {
                      trackSelectNavCta({ 'cta url': url })
                      setIsMobileMenuOpen(false)
                      // Navigate to the URL
                      if (url.startsWith('http')) {
                        window.location.href = url
                      } else {
                        router.push(url)
                      }
                    }
                    item.onClick?.()
                  }}
                >
                  <span className="text-heading-display-3 leading-8 text-white">{item.title}</span>
                  <div className="flex size-6 items-center justify-center">
                    <ChevronRightIcon className="size-5 text-bodi-green-bright-500" />
                  </div>
                </button>

                {/* Child items for active item */}
                {isActive && activeItemChildren.length > 0 && (
                  <div className="flex flex-col gap-3 px-0">
                    {activeItemChildren.map((childItem, childIndex) => {
                      const hasMenu = Boolean(childItem.navItems ?? childItem.miniMenu)
                      return (
                        <button
                          key={`child-menu-${childIndex}`}
                          className="flex w-full items-center justify-between"
                          onClick={() => {
                            if (hasMenu) {
                              // Open third level menu or mini menu
                              setSelectedThirdLevelMenu(childItem)
                            } else if (childItem.href) {
                              trackSelectNavCta({ 'cta url': childItem.href })
                              setIsMobileMenuOpen(false)
                            }
                            childItem.onClick?.()
                          }}
                        >
                          <span className="text-body-base leading-6 font-medium text-white">
                            {childItem.title}
                          </span>
                          {hasMenu && (
                            <div className="flex size-6 items-center justify-center">
                              <ChevronRightIcon className="size-5 text-bodi-gray-medium" />
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Secondary Menu Items */}
        {mobileSecondaryMenuItems && mobileSecondaryMenuItems.length > 0 && (
          <div className="flex flex-col gap-3">
            {mobileSecondaryMenuItems
              .filter((item) => {
                const visibility = item.visibility ?? 'always'
                if (visibility === 'always') return true
                if (visibility === 'loggedIn') return isLoggedIn
                if (visibility === 'loggedOut') return !isLoggedIn
                return true
              })
              .map((item, index) => (
                <button
                  key={`secondary-menu-${index}`}
                  className="flex w-full items-center justify-between"
                  onClick={() => {
                    if (item.href) {
                      trackSelectNavCta({ 'cta url': item.href })
                      setIsMobileMenuOpen(false)
                    } else {
                      setIsMobileMenuOpen(false)
                    }
                    item.onClick?.()
                  }}
                >
                  <span className="text-body-base leading-6 font-medium text-bodi-gray-butters">
                    {item.title}
                  </span>
                  <div className="flex size-6 items-center justify-center">
                    <ChevronRightIcon className="size-5 text-bodi-gray-medium" />
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
      <TopNavV6MobileFooter {...props} />
    </>
  )
}
